import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { fmtMoney, digitsOnly, groupThousands } from '../../../common/utils/money';
import { useCreateExchange } from '../contexts/exchanges';

import '../styles/billing.css';

const today = () => new Date().toLocaleDateString('en-CA');
const KINDS = [{ value: 'charge', label: 'Cargo (el dueño debe)' }, { value: 'credit', label: 'Haber (se le acredita)' }];
const SOURCES = [{ value: 'manual', label: 'Manual' }, { value: 'milk', label: 'Leche' }, { value: 'medical', label: 'Médico' }, { value: 'other', label: 'Otro' }];

function MoneyInput({ value, onChange, disabled }) {
    return (
        <span className="billing-money">
            <span className="billing-money__sign">$</span>
            <input type="text" inputMode="numeric" value={groupThousands(value)} onChange={(e) => onChange(digitsOnly(e.target.value))} disabled={disabled} placeholder="0" />
        </span>
    );
}

// Ajuste del período sobre una vaca (admin-billing-exchanges.md). El `amount` lo deriva
// el servidor (unit_cost × quantity); acá se muestra el preview. Se abre desde la
// liquidación de la vaca.
export default function ExchangeForm({ animalId, year, month, onClose, onSaved }) {
    const { mutateAsync: create, isPending: busy } = useCreateExchange();
    const cardRef = useModalDialog(onClose);

    const [kind, setKind] = useState('charge');
    const [source, setSource] = useState('manual');
    const [event, setEvent] = useState('');
    const [date, setDate] = useState(today());
    const [unitCost, setUnitCost] = useState('');
    const [qty, setQty] = useState('1');

    const preview = (Number(unitCost) || 0) * (Number(qty) || 0);

    const submit = async () => {
        if (unitCost === '' || unitCost == null) { toast.error('Ingresá el valor unitario'); return; }
        if (!(Number(qty) > 0)) { toast.error('La cantidad debe ser mayor a 0'); return; }
        if (!date) { toast.error('Elegí la fecha'); return; }
        const payload = {
            period_year: Number(year), period_month: Number(month), animal_id: Number(animalId),
            kind, source, event_date: date, unit_cost: Number(unitCost), quantity: Number(qty),
        };
        if (event.trim()) payload.event = event.trim();
        try {
            await create(payload);
            toast.success('Ajuste registrado');
            onSaved?.();
        } catch (er) {
            if (er?.status !== 403) toast.error(er?.message || 'No se pudo registrar el ajuste');
        }
    };

    return (
        <div className="billing-modal" role="dialog" aria-modal="true" aria-label="Nuevo ajuste">
            <div className="billing-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="billing-modal__card" ref={cardRef}>
                <header className="billing-modal__head">
                    <h2 className="billing-modal__title">Nuevo ajuste</h2>
                    <button type="button" className="billing-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="billing-form">
                    <div className="billing-form__row">
                        <label className="billing-form__field">
                            <span className="billing-form__label">Tipo</span>
                            <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={busy}>
                                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                            </select>
                        </label>
                        <label className="billing-form__field">
                            <span className="billing-form__label">Origen</span>
                            <select value={source} onChange={(e) => setSource(e.target.value)} disabled={busy}>
                                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </label>
                    </div>

                    <label className="billing-form__field">
                        <span className="billing-form__label">Concepto</span>
                        <input type="text" value={event} onChange={(e) => setEvent(e.target.value)} disabled={busy} placeholder="Ej. Antibiótico, leche…" />
                    </label>

                    <div className="billing-form__row">
                        <label className="billing-form__field">
                            <span className="billing-form__label">Valor unitario</span>
                            <MoneyInput value={unitCost} onChange={setUnitCost} disabled={busy} />
                        </label>
                        <label className="billing-form__field">
                            <span className="billing-form__label">Cantidad</span>
                            <input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} disabled={busy} />
                        </label>
                    </div>

                    <label className="billing-form__field">
                        <span className="billing-form__label">Fecha</span>
                        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                    </label>

                    <p className="billing-form__preview">Monto: <strong>{fmtMoney(preview)}</strong> <span>({kind === 'credit' ? 'haber' : 'cargo'})</span></p>
                </div>

                <footer className="billing-modal__foot">
                    <button type="button" className="billing-btn billing-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="billing-btn billing-btn--primary" onClick={submit} disabled={busy || unitCost === ''}>{busy ? 'Guardando…' : 'Registrar'}</button>
                </footer>
            </div>
        </div>
    );
}
